/**
 * `buildCanonicalRanking` — client copy of
 * mini-apps/action-view/priority-rank.ts's `buildCanonicalRanking`, re-keyed
 * onto the shared `PriorityRankingEntry` (`@shared/schemas/priority-ranking`,
 * bead .8) instead of the mini-app's own `RankingEntry` — never imported
 * (mini-apps/CLAUDE.md: core/web cannot import mini-app code); kept in
 * lockstep by convention and matching tests on both sides.
 *
 * Carries the SAME wildcard fallback precedence as
 * `src/services/flow/priority-config.ts`'s `buildPriorityRanking` (bead .8's
 * "the one sanctioned Action View edit, mirrored identically" on the
 * mini-app side): a lookup key resolves through a three-level precedence
 * chain, stopping at the first level with ANY matching entry (active or
 * deactivated):
 *   1. the exact key — `type|subtype|playType`
 *   2. the play-specific wildcard — `type|*|playType`
 *   3. the type-wide wildcard — `type|*|`
 * An entry's mere PRESENCE at a level is authoritative for that level — the
 * chain never skips a deactivated entry hunting for a broader active
 * fallback. Only when no level has any matching entry does the lookup fall
 * through to unconfigured (`rankOf` → undefined, `isDeactivated` → false).
 */
import type { PriorityRankingEntry } from '@shared/schemas/priority-ranking';

export interface CanonicalRanking {
  /** 0-based canonical rank, or undefined if the entry isn't configured/active. */
  rankOf(
    type: string | null | undefined,
    subtype: string | null | undefined,
    playType: string | null | undefined,
  ): number | undefined;
  /** True only for explicitly `active: false` entries (resolved through the wildcard chain). */
  isDeactivated(
    type: string | null | undefined,
    subtype: string | null | undefined,
    playType: string | null | undefined,
  ): boolean;
  /** Min canonical rank across a type's active entries; Infinity if unconfigured. */
  typeMinRank(type: string | null | undefined): number;
}

function entryKey(
  type: string | null | undefined,
  subtype: string | null | undefined,
  playType: string | null | undefined,
): string {
  return `${type ?? ''}|${subtype ?? ''}|${playType ?? ''}`;
}

/** Build a CanonicalRanking from a config's ranking entries (pure — no I/O). */
export function buildCanonicalRanking(entries: PriorityRankingEntry[]): CanonicalRanking {
  const activeEntries = entries
    .map((entry, listedIndex) => ({ entry, listedIndex }))
    .filter(({ entry }) => entry.active);

  // Type's first-listed ACTIVE index — the tiebreak that keeps a scattered
  // type's entries clustered together in canonical order.
  const typeFirstIndex = new Map<string, number>();
  for (const { entry, listedIndex } of activeEntries) {
    if (!typeFirstIndex.has(entry.type)) typeFirstIndex.set(entry.type, listedIndex);
  }

  const sorted = [...activeEntries].sort((a, b) => {
    const fa = typeFirstIndex.get(a.entry.type) ?? Infinity;
    const fb = typeFirstIndex.get(b.entry.type) ?? Infinity;
    if (fa !== fb) return fa - fb;
    return a.listedIndex - b.listedIndex;
  });

  const rankMap = new Map<string, number>();
  sorted.forEach(({ entry }, canonicalIndex) => {
    rankMap.set(entryKey(entry.type, entry.subtype, entry.playType), canonicalIndex);
  });

  const deactivatedSet = new Set<string>();
  for (const entry of entries) {
    if (!entry.active) deactivatedSet.add(entryKey(entry.type, entry.subtype, entry.playType));
  }

  const typeMinRankMap = new Map<string, number>();
  for (const { entry } of activeEntries) {
    const rank = rankMap.get(entryKey(entry.type, entry.subtype, entry.playType));
    if (rank == null) continue;
    const cur = typeMinRankMap.get(entry.type);
    if (cur == null || rank < cur) typeMinRankMap.set(entry.type, rank);
  }

  // Every key any entry (active or deactivated) is stored under — used to
  // decide whether the exact key or a wildcard is authoritative below.
  const allKeys = new Set<string>();
  for (const entry of entries) allKeys.add(entryKey(entry.type, entry.subtype, entry.playType));

  function resolveKey(
    type: string | null | undefined,
    subtype: string | null | undefined,
    playType: string | null | undefined,
  ): string | undefined {
    const exact = entryKey(type, subtype, playType);
    if (allKeys.has(exact)) return exact;
    const playSpecificWildcard = entryKey(type, '*', playType);
    if (allKeys.has(playSpecificWildcard)) return playSpecificWildcard;
    const typeWideWildcard = entryKey(type, '*', undefined);
    if (allKeys.has(typeWideWildcard)) return typeWideWildcard;
    return undefined;
  }

  return {
    rankOf: (type, subtype, playType) => {
      const key = resolveKey(type, subtype, playType);
      return key === undefined ? undefined : rankMap.get(key);
    },
    isDeactivated: (type, subtype, playType) => {
      const key = resolveKey(type, subtype, playType);
      return key === undefined ? false : deactivatedSet.has(key);
    },
    typeMinRank: (type) => typeMinRankMap.get(type ?? '') ?? Infinity,
  };
}
